import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';

export interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'error';
  onUndo?: () => void;
  duration?: number;
}

interface ToastProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

const CheckIcon = () => (
  <svg className="toast-icon w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const XCircleIcon = () => (
  <svg className="toast-icon w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 9l-6 6M9 9l6 6" />
  </svg>
);

const UndoIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 0 1 0 10H9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 6 3 10l4 4" />
  </svg>
);

const SingleToast: React.FC<{ toast: ToastItem; onDismiss: (id: string) => void; index: number }> = ({ toast, onDismiss, index }) => {
  const [isExiting, setIsExiting] = useState(false);
  const duration = toast.duration ?? (toast.type === 'error' ? 5000 : 3500);

  const dismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 250);
  }, [onDismiss, toast.id]);

  useEffect(() => {
    const timer = setTimeout(dismiss, duration);
    return () => clearTimeout(timer);
  }, [dismiss, duration]);

  const isSuccess = toast.type === 'success';

  return (
    <div
      className={`toast-item ${toast.type} ${isExiting ? 'toast-exit' : 'toast-enter'}`}
      role="alert"
      aria-live="polite"
      style={{ '--toast-index': index } as React.CSSProperties}
    >
      <div className="flex items-center gap-3 min-w-0">
        {isSuccess ? <CheckIcon /> : <XCircleIcon />}
        <span className="leading-snug">{toast.message}</span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 ml-2">
        {toast.onUndo && (
          <button
            onClick={() => { toast.onUndo?.(); dismiss(); }}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold underline-offset-2 hover:underline"
          >
            <UndoIcon />
            Rückgängig
          </button>
        )}
        <button
          onClick={dismiss}
          className="p-1 rounded opacity-60 hover:opacity-100"
          aria-label="Schließen"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const Toast: React.FC<ToastProps> = ({ toasts, onDismiss }) => {
  // Show max 3 toasts
  const visibleToasts = toasts.slice(-3);

  if (visibleToasts.length === 0) return null;

  return ReactDOM.createPortal(
    <div className="toast-container">
      {visibleToasts.map((toast, i) => (
        <SingleToast key={toast.id} toast={toast} onDismiss={onDismiss} index={i} />
      ))}
    </div>,
    document.body
  );
};

export default Toast;
